import type { EffectivePerk } from './entities/user.entity';

/**
 * Leituras sobre o conjunto **efetivo** de um perfil que o editor de acessos usa
 * para não deixar o operador conceder e revogar no escuro
 * (`docs/reference/modules/delivery.md` no monolith, rota `/effective`).
 *
 * A fonte é o `derivedFrom` que o back devolve, não o `grantsPerkKeys` cru do
 * catálogo: o primeiro já passou pelo scope do perfil e pelo portão; o segundo é
 * a intenção do grafo, e prometer um filho que esta pessoa não recebeu é pior
 * que não marcar nada.
 */

/**
 * Os `perkId`s que continuam no efetivo sem os pacotes `revokedGrantIds`: os
 * concedidos que sobram e, por ponto fixo, todo derivado com algum pai que
 * sobra. É o fecho refeito só sobre o `derivedFrom` que o back já filtrou por
 * scope e portão.
 */
function survivingPerkIds(
  revokedGrantIds: Set<string>,
  effective: EffectivePerk[],
): Set<string> {
  const surviving = new Set<string>();
  for (const perk of effective) {
    if (perk.derived || perk.grantedPerkId === null) continue;
    if (!revokedGrantIds.has(perk.grantedPerkId)) surviving.add(perk.perkId);
  }
  // O conjunto só cresce e é limitado pelo número de perks — o laço termina.
  const derived = effective.filter((perk) => perk.derived);
  for (let changed = true; changed; ) {
    changed = false;
    for (const perk of derived) {
      if (surviving.has(perk.perkId)) continue;
      if (perk.derivedFrom.some((parent) => surviving.has(parent.perkId))) {
        surviving.add(perk.perkId);
        changed = true;
      }
    }
  }
  return surviving;
}

/**
 * O que revogar **este pacote** derruba junto: os derivados que somem do efetivo
 * sem ele. É o efeito, não a topologia — a cauda é transitiva (`A→C→E` lista `C`
 * e `E`), e um filho que outro pacote também sustenta **não** entra: revogar só
 * este não o tira. Vazio = não é agregador para este perfil.
 */
export function aggregatedTail(
  grantedPerkId: string,
  effective: EffectivePerk[],
): EffectivePerk[] {
  const surviving = survivingPerkIds(new Set([grantedPerkId]), effective);
  return effective.filter(
    (perk) => perk.derived && !surviving.has(perk.perkId),
  );
}

/** Os derivados do perfil — o bloco "Também tem por composição". */
export function derivedPerks(effective: EffectivePerk[]): EffectivePerk[] {
  return effective.filter((perk) => perk.derived);
}

/** O derivado com este `perkId`, se o perfil já o tem por composição. */
export function findDerived(
  perkId: string,
  effective: EffectivePerk[],
): EffectivePerk | undefined {
  return effective.find((perk) => perk.derived && perk.perkId === perkId);
}

/**
 * Se, depois de revogar os pacotes `revokedGrantIds`, este perk continua vindo
 * de outro perk que sobra — devolve as keys que o sustentariam; vazio se o
 * acesso cai de fato.
 *
 * `grantsPerkKeysOf` é o do catálogo: o perk revogado é **concedido**, então o
 * back não o lista em `derivedFrom` (concessão se explica pelo ledger), e só o
 * grafo diz quem o derivaria na falta do pacote. `isWindow` é a mesma regra do
 * fecho — crédito nunca é derivado.
 *
 * É aviso, não autorização: o portão `requiresPerkKeys` não é reavaliado aqui,
 * e um nó que só passaria a ser derivado depois da revogação não entra na conta.
 * Os dois erros possíveis são para o lado de **não** avisar.
 */
export function sustainersAfterRevocation(input: {
  perkId: string;
  perkKey: string;
  isWindow: boolean;
  revokedGrantIds: Set<string>;
  effective: EffectivePerk[];
  grantsPerkKeysOf: (perkId: string) => string[];
}): string[] {
  const { perkId, perkKey, isWindow, revokedGrantIds, effective } = input;
  if (!isWindow) return [];

  const surviving = survivingPerkIds(revokedGrantIds, effective);
  // Outro pacote do mesmo perk continua (o back não lista concedido como
  // derivado, então só isso o põe aqui): o acesso não cai, mas não é por
  // composição — o aviso não se aplica.
  if (surviving.has(perkId)) return [];

  const keyById = new Map(effective.map((perk) => [perk.perkId, perk.perkKey]));
  const sustainers = new Set<string>();
  for (const survivor of surviving) {
    if (survivor === perkId) continue;
    if (input.grantsPerkKeysOf(survivor).includes(perkKey)) {
      sustainers.add(keyById.get(survivor)!);
    }
  }
  return [...sustainers].sort();
}
